import { formatCurrencyRound } from '@utils/formatters.util';
import { Tier } from './types';
import { DiscountProgressItem } from './DiscountProgressItem';
import cx from '@utils/cx.util';
import { useEffect, useState } from 'react';

type Props = {
  currentProgress: number;
  tiers: Tier[];
  nextTierInfo: {
    threshold: number;
    amountNeeded: number;
    gift: string;
  } | null;
  currentSubtotal: number;
  className?: string;
  variant?: 'default' | 'transparent' | 'fixed-bottom';
};

export const TieredDiscountProgress = ({
  currentProgress,
  tiers,
  nextTierInfo,
  currentSubtotal,
  className = '',
  variant = 'default',
}: Props) => {
  const [lastTierThreshold, setLastTierThreshold] = useState(0);
  const [tierChangeCount, setTierChangeCount] = useState(0);

  useEffect(() => {
    if (
      tiers.length > 0 &&
      tiers[tiers.length - 1]?.threshold > 0 &&
      tiers[tiers.length - 1]?.threshold !== lastTierThreshold
    ) {
      setLastTierThreshold(tiers[tiers.length - 1]?.threshold);
      setTierChangeCount((prev) => prev + 1);
    }
  }, [tiers, lastTierThreshold]);

  return (
    <div
      className={cx(
        {
          'tw-rounded-button tw-bg-[#F8F8F8] tw-px-4 tw-py-2.5': variant === 'default',
          'tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[1003] tw-flex tw-flex-col tw-items-center tw-justify-center tw-bg-[#F8F8F8] tw-px-4 tw-py-3 tw-shadow-[-3px_-3px_6px_#00000029] md:tw-flex-row md:tw-items-start md:tw-gap-3 md:tw-py-4':
            variant === 'fixed-bottom',
        },
        className
      )}>
      <div
        className={cx({
          'tw-flex tw-w-full tw-shrink-0 tw-flex-col tw-items-center md:tw-w-auto md:tw-flex-row md:tw-gap-2 md:tw-pt-3':
            variant === 'fixed-bottom',
        })}>
        <p className='tw-mb-0 tw-text-base tw-font-bold tw-text-body'>Buy More & Save!</p>
        {nextTierInfo ? (
          <p
            className={cx('tw-mb-3 tw-text-sm tw-font-normal tw-text-body', {
              'tw-mb-0 tw-text-base': variant === 'fixed-bottom',
            })}>
            Spend {formatCurrencyRound(nextTierInfo.amountNeeded)} more to earn{' '}
            {nextTierInfo.gift || 'next discount'}
          </p>
        ) : (
          <p
            className={cx('tw-mb-3 tw-text-sm tw-font-normal tw-text-body', {
              'tw-mb-0 tw-text-base': variant === 'fixed-bottom',
            })}>
            You've unlocked all available discounts!
          </p>
        )}
      </div>

      <div
        className={cx('tw-relative tw-mt-5 tw-w-full tw-px-2', {
          'tw-mx-auto tw-max-w-[400px] md:tw-mx-0 md:tw-max-w-[700px]': variant === 'fixed-bottom',
        })}>
        <div className='tw-relative tw-z-[1] tw-mx-6 tw-bg-[#d1d1d1]'>
          <div
            className='tw-h-[6px] tw-rounded-xl tw-bg-[#5F8047] tw-transition-all tw-duration-300'
            style={{ width: `${currentProgress}%` }}></div>
        </div>
        <div className='-tw-mx-5 -tw-mt-[18px] tw-overflow-hidden'>
          <div
            key={tiers[tiers.length - 1]?.threshold || 0}
            className={cx('tw-relative tw-z-[2] tw-flex tw-justify-between', {
              [`animate-tier-progress_${tiers.length}`]: lastTierThreshold && tierChangeCount > 1,
            })}>
            {tiers.map((tier) => (
              <DiscountProgressItem
                key={tier.threshold}
                {...tier}
                isActive={currentSubtotal >= tier.threshold}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
